import React, { useCallback, useEffect, useState } from 'react';
import { adminFileAPI } from '../services/api';
import FileViewModal from './FileViewModal';

const UserDocumentsModal = ({ isOpen, onClose, user }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({ page: 1, limit: 5 });

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const userId = user?._id || user?.id;

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const fetchFiles = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const response = await adminFileAPI.getAllSubmittedFiles({ ...filters, user_id: userId });
      if (response.data?.success) {
        setFiles(response.data.data.files || []);
        setPagination(response.data.data.pagination || {});
      } else {
        setError(response.data?.message || 'Failed to fetch documents');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  }, [filters, userId]);

  useEffect(() => {
    if (isOpen) {
      setFilters({ page: 1, limit: 5 });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && userId) fetchFiles();
  }, [isOpen, userId, fetchFiles]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1050,
      padding: '1.5rem',
    }}>
      <div style={{
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--border-radius)',
        border: '1px solid var(--border-color)',
        width: '100%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'var(--shadow-lg)'
      }}>
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0 }}>User Documents</h3>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {user?.name || user?.email || 'User'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.25rem', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>

        <div className="card-body" style={{ overflow: 'auto' }}>
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>
          )}

          {loading && files.length === 0 ? (
            <div className="d-flex justify-center align-center" style={{ minHeight: '300px' }}>
              <div className="spinner"></div>
            </div>
          ) : files.length === 0 ? (
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3rem', opacity: 0.5, marginBottom: '0.5rem' }}>📂</div>
                <div style={{ color: 'var(--text-secondary)' }}>No documents found for this user</div>
              </div>
            </div>
          ) : (
            <div className="file-list">
              {files.map((file) => (
                <div key={file._id} className="file-item">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                      {file.title || file.original_filename}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                      {file.description || 'No description'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      Uploaded: {formatDate(file.created_date)}
                    </div>
                  </div>
                  <div className="d-flex align-center" style={{ gap: '0.5rem' }}>
                    <button
                      onClick={() => { setSelectedFile(file); setIsViewOpen(true); }}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="card-footer">
            <div className="d-flex justify-between align-center">
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Showing {((pagination.currentPage - 1) * pagination.filesPerPage) + 1} to {Math.min(pagination.currentPage * pagination.filesPerPage, pagination.totalFiles)} of {pagination.totalFiles} files
              </div>
              <div className="d-flex" style={{ gap: '0.5rem' }}>
                <button
                  onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={!pagination.hasPrevPage}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  Previous
                </button>
                <span style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={!pagination.hasNextPage}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Nested File View Modal */}
      <FileViewModal
        file={selectedFile}
        isOpen={isViewOpen}
        onClose={() => { setIsViewOpen(false); setSelectedFile(null); }}
        onStatusUpdate={async () => ({ success: true })}
      />
    </div>
  );
};

export default UserDocumentsModal;


